import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ContextService } from '@maximizer/core/shared/data-access';
import { Octopus, TimelineEvent } from '@maximizer/core/shared/domain';
import { TimelineService } from '@maximizer/core/timeline/data-access';
import { catchError, map, Observable, of, tap } from 'rxjs';
import { CrmCitationRecord, toCrmCitationRecord } from './crm-citation.model';

/**
 * Reads the records an answer cites, so their chips show the record's own words rather than the
 * type and date the model wrote. One read covers every key of an answer, kept for the session.
 */
@Injectable({ providedIn: 'root' })
export class CrmCitationService {
  private readonly http = inject(HttpClient);
  private readonly context = inject(ContextService);
  private readonly timeline = inject(TimelineService);

  // null marks a key the CRM did not return, so it is not asked for again.
  private readonly cache = new Map<string, CrmCitationRecord | null>();

  resolve(
    keys: readonly string[],
  ): Observable<ReadonlyMap<string, CrmCitationRecord>> {
    const wanted = [...new Set(keys)].filter(Boolean);
    const missing = wanted.filter((key) => !this.cache.has(key));

    if (missing.length === 0) {
      return of(this.known(wanted));
    }

    return this.read(missing).pipe(
      tap((events) => {
        for (const key of missing) {
          this.cache.set(key, null);
        }
        for (const event of events) {
          if (event.key) {
            this.cache.set(event.key, toCrmCitationRecord(event));
          }
        }
      }),
      map(() => this.known(wanted)),
      // A failed read leaves the chips at type and date; nothing is cached, so the next answer
      // that cites these records asks again.
      catchError(() => of(this.known(wanted))),
    );
  }

  private known(keys: string[]): ReadonlyMap<string, CrmCitationRecord> {
    const records = new Map<string, CrmCitationRecord>();

    for (const key of keys) {
      const record = this.cache.get(key);
      if (record) {
        records.set(key, record);
      }
    }

    return records;
  }

  // TimelineService.forEvent for several keys at once; a change there belongs here too.
  private read(keys: string[]): Observable<TimelineEvent[]> {
    const request: Octopus.TimelineReadRequest = {
      Timeline: {
        Scope: { Fields: this.timeline.fields },
        Criteria: { SearchQuery: { Key: { $IN: keys } } },
      },
      Globalization: Octopus.DefaultGlobalization,
      Compatibility: { TaskObject: '2.0' },
    };

    return this.http
      .post<Octopus.TimelineResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          // A failure comes back as a code, not as an HTTP error: it is not "no records".
          if (result.Code !== Octopus.ResponseStatusCode.Successful) {
            throw new Error(`Timeline read failed: code ${result.Code}`);
          }

          return result.Timeline?.Data
            ? new Octopus.TimelineMapper().getEvents(result.Timeline.Data)
            : [];
        }),
      );
  }
}
